import {PageHeading} from '@/components/ui/page-atoms/PageAtoms'
import {AlertTriangleIcon} from '@/components/ui/icon/Icon'
import {StyledNameCell, StyledUsersWrapper} from './style'
import {useGetOperativeUsers} from '@/features/users/queries/useGetOperativeUsers.ts'
import {useGetTherapistRole} from '@/features/users/queries/useGetTherapistRole.ts'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {Avatar} from '@components/ui/avatar/Avatar.tsx'
import i18n, {useTranslation} from '@/translations/i18n.tsx'
import {ChangeEvent, useRef} from 'react'
import {ErrorBox} from '@components/commons/error-box/ErrorBox.tsx'
import {Table} from '@components/commons/table/Table.tsx'
import {operativeUsersColumnToLabel, useUsersSearchParams} from '@/features/users/utils.ts'
import {Helmet} from 'react-helmet'
import {
    columnVisibilitySearchParamsToPayload,
    columnVisibilityToSearchParams,
    debounce,
    filtersParamToPayload,
    sortParamToPayload
} from '@utilities/helpers.ts'
import {Button} from '@components/ui/button/Button.tsx'
import {ColumnVisibilityMenu} from '@components/commons/columns-visibility-menu/ColumnVisibilityMenu.tsx'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {TableBodyCell, TableBodyCellText} from '@components/ui/table-atoms/TableAtoms.tsx'
import {FiltersBar} from '@components/ui/filters-bar/FiltersBar.tsx'
import {TableInputSearch} from '@components/commons/table-input-search/TableInputSearch.tsx'
import {TableBodyRow} from '@components/ui/table-body-row/TableBodyRow.tsx'
import {OperativeUsersColumn} from '@/features/users/types'
import {TableFilters} from '@components/commons/table-filters/TableFilters.tsx'
import {SortingMenu} from '@/components/commons/sorting-menu/SortingMenu'

const makeColumnsMap = () =>
    OperativeUsersColumn.options.map(column => ({
        id: column,
        label: i18n.t(operativeUsersColumnToLabel[column])
    }))

/* Reduced variant of the users list (`/users`), rendered by `Users` for `users.read` holders
without `users.write`: rows are not clickable and there is no row actions menu, since both the
user details and the row actions require write access */
export const OperationalUsers = () => {
    const {t} = useTranslation()
    const columnsMap = makeColumnsMap()
    const {searchParams, setSearchParams} = useUsersSearchParams()
    const inputSearchRef = useRef<HTMLInputElement | null>(null)
    const role = useGetTherapistRole()

    /* Safety invariants of this list: the therapist role is always the first field and the
    operator is always `and`, so the user filters can only narrow the fixed scope. The `roleId`
    field never reaches the URL — only the user filters live in `searchParams.filters`. */
    const payload: NonNullable<Parameters<typeof useGetOperativeUsers>[1]> = {
        filters: {
            operator: 'and',
            fields: [
                {key: 'roleId', operator: 'in', value: role.roleId ? [role.roleId] : []},
                ...(filtersParamToPayload(searchParams.filters)?.filters?.fields ?? [])
            ]
        },
        sorts: sortParamToPayload(searchParams.sorts)
    }
    const usersQuery = useGetOperativeUsers(
        {
            limit: searchParams.limit,
            page: searchParams.page,
            search: searchParams.search,
            responseType: 'index'
        },
        payload,
        {enabled: !!role.roleId}
    )

    const onSearch = debounce((event: ChangeEvent<HTMLInputElement>) => {
        setSearchParams({search: event.target.value || undefined, page: 1})
    }, 1000)

    const columnVisibility =
        columnVisibilitySearchParamsToPayload(OperativeUsersColumn, searchParams.columnVisibility) ??
        Object.fromEntries(OperativeUsersColumn.options.map(column => [column, true]))

    return (
        <StyledUsersWrapper>
            <Helmet title={t('users:meta_title')} />
            <PageHeading justify="space-between">
                <h2>{t('commons:users')}</h2>
            </PageHeading>
            <FiltersBar>
                <Flexbox align="center" gap={2}>
                    <TableInputSearch
                        ref={inputSearchRef}
                        defaultValue={searchParams.search}
                        onChange={onSearch}
                        placeholder={t('commons:search_name')}
                    />
                </Flexbox>
                <Flexbox style={{flexShrink: 0}} align="center" gap={2}>
                    <TableFilters
                        onApply={filters => setSearchParams({filters: JSON.stringify(filters), page: 1})}
                        filtersParams={searchParams.filters}
                        entity="users"
                        isOperatorLocked
                        availableFilters={[
                            {
                                key: OperativeUsersColumn.enum.specializationId,
                                label: operativeUsersColumnToLabel.specializationId
                            },
                            {
                                key: OperativeUsersColumn.enum.contactEmail,
                                label: operativeUsersColumnToLabel.contactEmail
                            },
                            {
                                key: OperativeUsersColumn.enum.mobilePhone,
                                label: operativeUsersColumnToLabel.mobilePhone
                            }
                        ]}
                    />
                    <SortingMenu
                        columns={columnsMap}
                        order={sortParamToPayload(searchParams.sorts) ?? []}
                        setOrder={sorts => setSearchParams({sorts: JSON.stringify(sorts)})}
                    />
                    <ColumnVisibilityMenu
                        columns={columnsMap}
                        columnVisibility={columnVisibility}
                        onSubmit={columnVisibility =>
                            setSearchParams({columnVisibility: columnVisibilityToSearchParams(columnVisibility)})
                        }
                    />
                </Flexbox>
            </FiltersBar>
            {role.isError || usersQuery.isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : role.isNotFound ? (
                <EmptyState
                    title={t('users:operational_users_role_missing_title')}
                    subtitle={t('users:operational_users_role_missing_description')}
                />
            ) : (
                <Table
                    emptyStateComponent={
                        <EmptyState
                            title={t('users:no_operational_users_title')}
                            subtitle={t('users:no_operational_users_description')}
                        >
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                    setSearchParams({search: undefined, filters: undefined, page: 1})
                                    if (inputSearchRef.current) {
                                        inputSearchRef.current.value = ''
                                    }
                                }}
                            >
                                {t('users:no_operational_users_cta')}
                            </Button>
                        </EmptyState>
                    }
                    rowComponent={({tableBodyRowProps, index}) => (
                        <TableBodyRow key={index} style={{cursor: 'default'}} {...tableBodyRowProps} />
                    )}
                    pagination={{
                        ...usersQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: searchParams.limit,
                        onChangePage: page => setSearchParams({page}),
                        onChangeLimit: limit => setSearchParams({limit, page: 1})
                    }}
                    columnPinning={{left: [OperativeUsersColumn.enum.fullName]}}
                    columnVisibility={columnVisibility}
                    isLoading={role.isPending || usersQuery.isPending}
                    data={usersQuery.data?.data ?? []}
                    columns={[
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledNameCell>
                                        <Avatar
                                            imageUrl={dataItem.avatarUrl ?? undefined}
                                            size="sm"
                                            name={dataItem.fullName}
                                        />
                                        <TableBodyCellText>{dataItem.fullName}</TableBodyCellText>
                                    </StyledNameCell>
                                </TableBodyCell>
                            ),
                            header: () => t(operativeUsersColumnToLabel.fullName),
                            id: OperativeUsersColumn.enum.fullName,
                            minSize: () => 80,
                            size: () => 250,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.specializationName}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(operativeUsersColumnToLabel.specializationId),
                            id: OperativeUsersColumn.enum.specializationId,
                            minSize: () => 80,
                            size: () => 140,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.email}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(operativeUsersColumnToLabel.contactEmail),
                            id: OperativeUsersColumn.enum.contactEmail,
                            minSize: () => 80,
                            size: () => 200,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.phoneNumber}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(operativeUsersColumnToLabel.mobilePhone),
                            id: OperativeUsersColumn.enum.mobilePhone,
                            size: () => 160,
                            maxSize: () => 400
                        }
                    ]}
                />
            )}
        </StyledUsersWrapper>
    )
}
